import {
    Component,
    h,
    Host,
    Listen,
    Method,
    Prop,
    State,
    Watch,
} from '@stencil/core';
import {
    getCachedTheme,
    Languages,
    ServiceType,
    ThemeType,
} from '@uipath/portal-shell-util';

import { LanguageEvent } from '../../models/appInsights/events/language.events';
import { ProfileMenuEvent } from '../../models/appInsights/events/profile-menu.events';
import { ThemeEvent } from '../../models/appInsights/events/theme.events';
import { ProfileMenuClickedEvent } from '../../models/events/profileClickedEvent';
import { ProductInfo } from '../../models/productInfo';
import store from '../../store/store';
import { accountAndTenantsStore } from '../../utils/accountAndTenants/accountAndTenants';
import {
    getFriendlyAccountTypeName,
    isEnterpriseOrProOrTrial,
} from '../../utils/accountAndTenants/accountAndTenantsHelpers';
import {
    isLocal,
    isMsi,
    isMsiOrStandalone,
} from '../../utils/EnvironmentUtil';
import { EventHandlersInstanceResolver } from '../../utils/events/eventHandlersInstanceResolver';
import GlobalEventEmitter from '../../utils/events/globalEventEmitters';
import { Features } from '../../utils/featureFlags/featureFlagConstants';
import { getFeatureFlag } from '../../utils/featureFlags/featureFlags';
import { parseArrayProps } from '../../utils/helper';
import {
    getLanguage,
    setSelectedLanguage,
} from '../../utils/languageHelper';
import {
    getLanguageText,
    t,
} from '../../utils/localization/loc';
import Logger from '../../utils/logger/logger';
import { getOrganizationStandardUrl } from '../../utils/orgStandardUrlHelper';
import {
    getTheme,
    setSelectedTheme,
} from '../../utils/themeHelper';
import { MenuState } from './MenuState';

@Component({
    tag: 'portal-profile-menu',
    styleUrl: 'portal-profile-menu.scss',
    shadow: true,
})
export class PortalProfileMenu {
    /**
    * Theme override to bypass store state
    * @memberof PortalProfileMenu
    */
    @Prop() theme: ThemeType = 'autoTheme';

    /**
     * Show the preferences button and hide the language and theme picker
     */
    @Prop() showPreferences: boolean = true;

    /**
     * The email address shown in the profile menu
     */
    @Prop() email: string;

    /**
     * The full name shown in the profile menu
     */
    @Prop() fullName: string;

    /**
     * The information to be displayed for the product
     */
    @Prop() productInfo?: ProductInfo | string;

    /**
     * Open upgrade dialog
     */
    @Prop() handleOpenDialog: () => void;

    /**
     * Thinner app-bar
     * @memberof PortalProfileMenu
     * @type {boolean}
     * @default true
     */
    @Prop() thin: boolean = true;

    /**
     * Comma-separated list of language codes to override the languages in the language list
     * @memberof PortalProfileMenu
     * @type {string}
     */
    @Prop() languages?: string;

    /**
     * Hide languages item
     * @memberof PortalProfileMenu
     * @type {boolean}
     */
    @Prop() hideLanguages: boolean = false;

    /**
     * Language
     * @memberof PortalProfileMenu
     * @type {string}
     */
    @Prop() language: string = 'en';

    /**
     * Show avatar slot
     * @memberof PortalProfileMenu
     * @type {boolean}
     * @default false
     */
    @Prop() showAvatarSlot: boolean = false;

    @State() menuState: MenuState = MenuState.profile;
    @State() productInfoValue?: ProductInfo;
    @State() menuOpen: boolean = false;

    private activeDropdownMenuSurface: HTMLPortalMenuSurfaceElement;
    private upgradeButtonItem: HTMLPortalButtonElement;
    private langBackButton: HTMLPortalIconButtonElement;
    private themeBackButton: HTMLPortalIconButtonElement;
    private languageSelectorItem: HTMLPortalProfileMenuItemElement;
    private themeList = [ 'autoTheme', 'dark', 'light' ];
    private shiftTabPressed = false;

    private rightSideAnchor = {
        top: this.thin ? '56px' : '46px',
        left: '',
        right: '10px',
    };

    @Watch('productInfo')
    onProductInfoChange(newValue?: ProductInfo | string) {
        if (typeof newValue === 'string') {
            try {
                this.productInfoValue = JSON.parse(newValue);
            } catch {
                Logger.error('portal-profile-menu', 'could not parse productInfo', { printToConsoleOnly: true });
            }
        } else {
            this.productInfoValue = newValue;
        }
    }

    /**
     * toggle menu surface visibility
     * @method PortalProfileMenu
     */
    @Method()
    async toggleProfileMenu() {
        this.menuState = MenuState.profile;
        return this.activeDropdownMenuSurface.toggleMenu();
    }

    /**
     * sets menu surface visible to false
     * @method PortalProfileMenu
     */
    @Method()
    async closeProfileMenu() {
        this.menuState = MenuState.profile;
        return this.activeDropdownMenuSurface.closeMenu();
    }

    @Listen('profileMenuItemClicked')
    onProfileMenuActivated(event: CustomEvent<ProfileMenuClickedEvent>) {
        Logger.info('portal-profile-menu', `profile menu item clicked: ${event.detail.id}`, { object: event.detail });
        switch (event.detail.id) {
            case 'language':
                this.menuState = MenuState.language;
                this.setLangBackButtonFocus();
                break;
            case 'theme':
                this.menuState = MenuState.theme;
                this.setThemeBackButtonFocus();
                break;
            case 'logout':
                Logger.info([ 'portal-profile-menu', 'session', 'click' ], 'logout clicked');
                GlobalEventEmitter.emitLogout();
                EventHandlersInstanceResolver.Instance?.onLogout?.();

                this.activeDropdownMenuSurface.closeMenu();

                // This is required for MSI to work properly
                if (isMsi()) {
                    window.PortalShell?.Session?.logout();
                }

                break;
            case 'preferences':
                if (this.showPreferences) {
                    this.openProfile();
                }
                break;
        }

        if (event.detail.id.startsWith('lang-')) {
            const language = event.detail.value;
            Logger.logCustomEvent(LanguageEvent.ProfileMenuLanguageClicked, { language });
            setSelectedLanguage(store, language);
        }

        if (event.detail.id.startsWith('theme-')) {
            const theme = event.detail.value as ThemeType;
            Logger.logCustomEvent(ThemeEvent.ProfileMenuThemeClicked, { theme });
            setSelectedTheme(store, theme);
        }
    }

    componentWillLoad() {
        this.onProductInfoChange(this.productInfo);
    }

    render() {
        const anchor = this.rightSideAnchor;
        const language = this.language;
        const selectedTheme = store.get('selectedTheme') || getCachedTheme() || this.theme;
        const accountType = accountAndTenantsStore.get('organization')?.accountType?.toUpperCase() ?? '';
        const itemRefs: HTMLPortalProfileMenuItemElement[] = [];

        let languageList = this.languages ? parseArrayProps(this.languages) : Object.keys(Languages);
        const testLanguageFlag = getFeatureFlag(Features.EnableLocKeysLanguage.name);
        if (!testLanguageFlag) {
            languageList = languageList.filter(key => key !== 'keys');
        }
        if (!getFeatureFlag(Features.EnableLanguageZhTw.name)) {
            languageList = languageList.filter(key => key !== 'zh-tw');
        }

        const fullName = this.fullName || t('avatar', language);

        return (
            <Host class={getTheme(store, this.theme)}>
                <button
                    class="portal-avatar-button"
                    data-test="avatar-section"
                    aria-haspopup="true"
                    aria-expanded={`${this.menuOpen}`}
                    aria-label={fullName}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                            this.closeProfileMenu();
                        }
                    }}
                    onClick={() => {
                        this.toggleProfileMenu();
                    }}
                >
                    {this.showAvatarSlot ? (
                        <slot name="avatar" />
                    ) : (
                        <portal-avatar fullName={fullName} theme={selectedTheme} />
                    )}
                </button>
                <portal-menu-surface
                    data-test="portal-profile-menu"
                    data-cy="portal-profile-menu"
                    top={anchor.top}
                    left={anchor.left}
                    right={anchor.right}
                    theme={this.theme}
                    onKeyDown={this.handleKeyDown}
                    onPortalMenuSurfaceOpened={() => this.menuOpen = true}
                    onPortalMenuSurfaceClosed={() => this.menuOpen = false}
                    ref={(elem) => this.activeDropdownMenuSurface = elem as HTMLPortalMenuSurfaceElement}>
                    <div class="profile-menu-res" id="profile-menu-content" data-test="profile-menu-content">
                        {
                            this.menuState === MenuState.profile &&
                            <div data-cy="profile-menu-list" id="profile-menu-list" data-test="profile-menu-list" role="list">
                                {!isEnterpriseOrProOrTrial(accountType)
                                    && getFeatureFlag(Features.EnableUpgradeDialogOffersRevamp.name) &&
                                [
                                    <div id="upgrade-section" role='listitem' key="upgrade-section">
                                        {this.getUpgradeSection(language, getFriendlyAccountTypeName(accountType))}
                                    </div>,
                                    <portal-divider type="horizontal" key="upgrade-section-divider"/>,
                                ]}
                                <div id="user-info" role='listitem'>
                                    {this.getProfileUserSection()}
                                </div>
                                <portal-divider type="horizontal" />
                                {
                                    this.showPreferences &&
                                    <portal-profile-menu-item
                                        id="preferences"
                                        ref={(ref: HTMLPortalProfileMenuItemElement) => {
                                            itemRefs.push(ref);
                                        }}
                                        text={t('profile-navigation', language)}
                                        href={this.getProfileUrl()}
                                        onBlur={() => this.setInteractiveElementFocus(itemRefs, true)}
                                    />
                                }
                                {
                                    this._showLanguageButton() &&
                                        <portal-profile-menu-item
                                            id="language"
                                            ref={(ref: HTMLPortalProfileMenuItemElement) => {
                                                this.languageSelectorItem = ref;
                                                itemRefs.push(ref);
                                            }}
                                            submenu={true}
                                            text={getLanguageText(language)}
                                            onBlur={() => {
                                                // Prevent handling since this will not be the first item in the menu
                                                if (this.showPreferences) {
                                                    return;
                                                }

                                                this.setInteractiveElementFocus(itemRefs, true);
                                            }}
                                        />
                                }
                                {
                                    this._showThemeButton() &&
                                        <portal-profile-menu-item
                                            id="theme"
                                            ref={(ref: HTMLPortalProfileMenuItemElement) => itemRefs.push(ref)}
                                            submenu={true}
                                            text={t(selectedTheme, language)}
                                            onBlur={() => {
                                                // Prevent handling since this will not be the first item in the menu
                                                if (this._showLanguageButton() || this.showPreferences) {
                                                    return;
                                                }

                                                this.setInteractiveElementFocus(itemRefs, true);
                                            }}
                                        />
                                }
                                <portal-profile-menu-item
                                    id="logout"
                                    text={t('logout', language)}
                                    ref={(ref: HTMLPortalProfileMenuItemElement) => itemRefs.push(ref)}
                                    onBlur={() => this.setInteractiveElementFocus(itemRefs)}
                                />
                                {
                                    this.productInfoValue && <div>
                                        <portal-divider type="horizontal" />
                                        <div data-cy="product-info-section" class="product-info-section" role='listitem'>
                                            <div data-cy="product-info-build-year">{`© UiPath ${this.productInfoValue.buildYear ?? ''}`}</div>
                                            <div data-cy="product-info-name-version">{`${this.productInfoValue.name ?? ''} ${this.productInfoValue.version ?? ''}`}</div>
                                        </div>
                                    </div>
                                }
                            </div>
                        }
                        {
                            this.menuState === MenuState.language &&
                                <div class="" data-cy="profile-language-list" role="list">
                                    <div class="menu-header">
                                        <portal-icon-button
                                            ref={(ref: HTMLPortalIconButtonElement) => this.langBackButton = ref}
                                            name="arrow_back"
                                            data-test="lang-back-btn"
                                            data-cy="lang-back-btn"
                                            size="sm"
                                            onClick={this.handleBackButtonClick} />
                                        <span class="text">{t('language', language)}</span>
                                    </div>
                                    {
                                        languageList.map((lang, index) =>
                                            <portal-profile-menu-item
                                                aria-label={getLanguageText(lang)}
                                                key={lang}
                                                id={`lang-${lang}`}
                                                role='listitem'
                                                selectable={true}
                                                active={lang === language}
                                                value={lang}
                                                text={getLanguageText(lang)}
                                                onBlur={() => {
                                                    if (index === languageList.length - 1) {
                                                        this.setLangBackButtonFocus();
                                                    }
                                                }}
                                            />,
                                        )
                                    }
                                </div>
                        }
                        {
                            this.menuState === MenuState.theme &&
                                <div class="" data-cy="profile-theme-list" role="list">
                                    <div class="menu-header">
                                        <portal-icon-button
                                            ref={(ref: HTMLPortalIconButtonElement) => this.themeBackButton = ref }
                                            name="arrow_back"
                                            data-test="theme-back-btn"
                                            data-cy="theme-back-btn"
                                            size="sm"
                                            onClick={this.handleBackButtonClick} />
                                        <span class="text">{t('theme', language)}</span>
                                    </div>
                                    {
                                        this.themeList.map((theme, index) =>
                                            <portal-profile-menu-item
                                                key={theme}
                                                role='listitem'
                                                selectable={true}
                                                active={theme === selectedTheme}
                                                id={'theme-' + theme}
                                                value={theme}
                                                text={t(theme, language)}
                                                onBlur={() => {
                                                    if (index === this.themeList.length - 1) {
                                                        this.setThemeBackButtonFocus();
                                                    }
                                                }}
                                            />,
                                        )
                                    }
                                </div>
                        }
                    </div>
                </portal-menu-surface>
            </Host>
        );
    }

    private _showLanguageButton() {
        return !this.hideLanguages && (!this.showPreferences || isLocal());
    }

    private _showThemeButton() {
        return (!this.showPreferences || isLocal()) && store.get('enableThemeSwitcher');
    }

    private getProfileUserSection() {
        return <div class="user-section">
            {
                this.fullName &&
                <div data-cy="portal-profile-menu-full-name" class="username">{this.fullName}</div>
            }
            {
                this.email &&
                <div data-cy="portal-profile-menu-email" class="email">{this.email}</div>
            }
        </div>;
    }

    private getUpgradeSection(language: string, license: string) {
        return <div class="upgrade-section">
            {
                <div data-cy="portal-profile-upgrade-section" class="current-plan">
                    {t('current_plan_upgrade', language, { license: t(license, language) })}
                </div>
            }
            <portal-button
                label={t('Upgrade', getLanguage(store))}
                type='text'
                size='sm'
                haspopup
                ref={(ref: HTMLPortalButtonElement) => this.upgradeButtonItem = ref}
                data-test="portal-profile-upgrade-button"
                onClick={() => {
                    this.handleOpenDialog();
                    this.closeProfileMenu();
                }}
            />
        </div>;
    }

    private setInteractiveElementFocus(itemRefs: HTMLPortalProfileMenuItemElement[], focusLast = false) {
        const shouldFocusFirst = !this.shiftTabPressed && !focusLast;
        const shouldFocusLast = this.shiftTabPressed && focusLast;

        // If we are shift tabbing from first, we need to focus from first button to last.
        // If we're tabbing from last, we need to focus from last button to first (otherwise skip)
        if (!shouldFocusFirst && !shouldFocusLast) {
            return;
        }

        if (this.upgradeButtonItem && !focusLast) {
            this.upgradeButtonItem?.shadowRoot?.querySelector('button')?.focus();
            return;
        }

        itemRefs[focusLast ? itemRefs.length - 1 : 0 ]?.shadowRoot?.querySelector('a')?.focus();
    }

    private setLangBackButtonFocus() {
        setTimeout(() => {
            this.langBackButton.shadowRoot?.querySelector('button')?.focus();
        }, 50);
    }

    private openProfile() {
        Logger.logCustomEvent(ProfileMenuEvent.PreferencesClicked);
        window.open(this.getProfileUrl(), '_blank');
    }

    private getProfileUrl() {
        const profileUrlOverride = store.get('profileUrlOverride');
        if (profileUrlOverride) {
            return profileUrlOverride;
        }

        const msiOrStandalone = isMsiOrStandalone();
        const organizationStandardUrl = getOrganizationStandardUrl({
            shouldIncludeAccountNameOnK8s: true,
            shouldAlwaysExcludeAccountName: msiOrStandalone,
        });
        const portalPrefix = msiOrStandalone
            ? `${organizationStandardUrl}/identity/management`
            : `${organizationStandardUrl}/${ServiceType.cloudRpa}_`;

        return `${portalPrefix}/profile`;
    }

    private setThemeBackButtonFocus() {
        setTimeout(() => {
            this.themeBackButton.shadowRoot?.querySelector('button')?.focus();
        }, 50);
    }

    private handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Tab') {
            this.shiftTabPressed = event.shiftKey;
        }
        if (event.key === 'Escape') {
            this.activeDropdownMenuSurface.closeMenu();
        }
    };

    private handleBackButtonClick = (_event: MouseEvent) => {
        this.menuState = MenuState.profile;
        this.languageSelectorItem?.shadowRoot?.querySelector('a')?.focus();
    };
}

